import { useRegion } from '@/contexts/RegionContext';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Quote, Star, Pause, Play } from 'lucide-react';
import localReviewsData from '../../data/reviews';
import { LazyImage } from '@/components/ui/LazyImage';

const API_KEY = (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_GOOGLE_MAPS_API_KEY) || (typeof process !== 'undefined' && process.env?.VITE_GOOGLE_MAPS_API_KEY) || "AIzaSyDTorU6LEGVhX01KOya3BNPVJoUzPexHcU";
const PLACE_ID = "ChIJq7gmuCRpzB0RaaOQ7fQcle8";

export function RotatingReviews() {
  const { formatDate } = useRegion();

  const [reviews, setReviews] = useState<any[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isPaused, setIsPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  useEffect(() => {
    let isMounted = true;
    
    const fetchReviews = async () => {
      try {
        // First try to use local data if available (e.g. from Google Takeout)
        if (Array.isArray(localReviewsData) && localReviewsData.length > 0) {
          const formattedLocalReviews = localReviewsData.map((r: any) => {
            // Support Google Business Profile Takeout format
            const ratingMap: Record<string, number> = {
              'ONE': 1, 'TWO': 2, 'THREE': 3, 'FOUR': 4, 'FIVE': 5
            };
            const rating = typeof r.starRating === 'string' ? ratingMap[r.starRating] || 5 : (r.rating || 5);
            
            return {
              rating,
              text: r.comment || r.text || "",
              author_name: r.reviewer?.displayName || r.author_name || "Google Reviewer",
              profile_photo_url: r.reviewer?.profilePhotoUrl || r.profile_photo_url || "",
              relative_time_description: r.createTime 
                ? formatDate(r.createTime, { year: 'numeric', month: 'long', day: 'numeric' })
                : (r.relative_time_description || "")
            };
          }).filter(r => r.rating >= 4);

          if (formattedLocalReviews.length > 0) {
            if (isMounted) {
              setReviews(formattedLocalReviews);
              setLoading(false);
            }
            return;
          }
        } else if (localReviewsData && typeof localReviewsData === 'object' && 'reviews' in localReviewsData && Array.isArray((localReviewsData as any).reviews) && (localReviewsData as any).reviews.length > 0) {
           // Support another common wrapper format { "reviews": [...] }
           const formattedLocalReviews = (localReviewsData as any).reviews.map((r: any) => {
             const ratingMap: Record<string, number> = {
              'ONE': 1, 'TWO': 2, 'THREE': 3, 'FOUR': 4, 'FIVE': 5
             };
             const rating = typeof r.starRating === 'string' ? ratingMap[r.starRating] || 5 : (r.rating || 5);
             return {
               rating,
               text: r.comment || r.text || "",
               author_name: r.reviewer?.displayName || r.author_name || "Google Reviewer",
               profile_photo_url: r.reviewer?.profilePhotoUrl || r.profile_photo_url || "",
               relative_time_description: r.createTime 
                 ? formatDate(r.createTime, { year: 'numeric', month: 'long', day: 'numeric' })
                 : (r.relative_time_description || "")
             };
           }).filter((r: any) => r.rating >= 4);

           if (formattedLocalReviews.length > 0) {
             if (isMounted) {
               setReviews(formattedLocalReviews);
               setLoading(false);
             }
             return;
           }
        }

        // Fallback to live Google Places API
        if (!API_KEY) {
          console.warn("No Google Maps API Key provided.");
          if (isMounted) {
            setReviews([]);
            setLoading(false);
          }
          return;
        }

        const response = await fetch(`https://places.googleapis.com/v1/places/${PLACE_ID}?fields=reviews&key=${API_KEY}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        
        if (!isMounted) return;
        
        if (data && data.reviews) {
          const highRatings = data.reviews.filter((r: any) => r.rating && r.rating >= 4);
          // Standard Google Places API limits reviews to 5.
          setReviews(highRatings);
        } else {
          console.warn("No reviews found in response.");
          setReviews([]);
        }
        setLoading(false);
      } catch (error) {
        if (!isMounted) return;
        console.warn("Failed to load Google Reviews:", error);
        setReviews([]);
        setLoading(false);
      }
    };

    fetchReviews();
    
    return () => {
      isMounted = false;
    };
  }, []);

  // Rotating logic
  useEffect(() => {
    if (reviews.length <= 1 || isPaused || isHovered) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % reviews.length);
    }, 8000); // Increased duration to 8s for longer reading time
    
    return () => clearInterval(interval);
  }, [reviews, isPaused, isHovered, currentIndex]);

  // Reset expanded state when changing review
  useEffect(() => {
    setExpanded(false);
  }, [currentIndex]);

  if (loading) {
    return (
      <div className="bg-ground p-10 lg:p-12 relative overflow-hidden h-full flex flex-col items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-2 border-rule-3 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (reviews.length === 0) {
    return null; // Hide section if no reviews loaded
  }

  const currentReview = reviews[currentIndex];
  
  // Handle both older JS SDK format and new REST format just in case
  const reviewText = currentReview.text?.text || currentReview.text || "";
  const authorName = currentReview.authorAttribution?.displayName || currentReview.author_name || "Google Reviewer";
  const authorPhoto = currentReview.authorAttribution?.photoUri || currentReview.profilePhotoUri || currentReview.profile_photo_url || "";
  const publishTime = currentReview.relativePublishTimeDescription || currentReview.relative_time_description || "";

  const isLongReview = reviewText.length > 250;
  const displayHtml = expanded ? reviewText : (isLongReview ? reviewText.substring(0, 250) + "..." : reviewText);

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="bg-ground p-10 lg:p-12 relative overflow-hidden h-full flex flex-col justify-center min-h-[450px]"
    >
      <Quote className="w-16 h-16 text-rule-3/20 absolute top-8 left-8" />
      
      <div className="relative z-10 w-full h-full flex flex-col">
        <div className="flex items-center gap-1 mb-6">
          {[...Array(5)].map((_, i) => (
            <Star 
              key={i} 
              className={`w-5 h-5 ${i < currentReview.rating ? 'fill-rule-3 text-rule-3' : 'text-ground-deep'}`} 
            />
          ))}
          <svg className="w-5 h-5 ml-2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
          </svg>
          <span className="ml-1 text-[11px] font-bold uppercase tracking-[0.15em] text-ink/70/60">
            Google Review
          </span>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.5 }}
            className="flex flex-col flex-1 pb-16"
          >
            <div className="flex-1 overflow-y-auto pr-4 mb-8 pt-2 custom-scrollbar">
              <p className="text-xl md:text-2xl font-serif italic text-ink leading-relaxed whitespace-pre-wrap">
                "{displayHtml}"
              </p>
              {isLongReview && (
                <button 
                  onClick={() => setExpanded(!expanded)}
                  className="text-rule-3 font-semibold text-sm mt-3 hover:text-ink transition-colors focus:outline-none"
                >
                  {expanded ? "Read less" : "Read more"}
                </button>
              )}
            </div>
            
            <div className="mt-auto flex items-center gap-4">
              {authorPhoto ? (
                <LazyImage src={authorPhoto} alt={authorName} className="w-12 h-12 rounded-full border border-ground-deep object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-full bg-ground-deep flex items-center justify-center text-ink/70 font-bold uppercase">
                  {authorName.charAt(0)}
                </div>
              )}
              <div>
                <div className="font-bold text-ink tracking-wide uppercase text-sm">
                  {authorName}
                </div>
                <div className="text-ink/70/70 text-sm mt-1">
                  {publishTime}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
      
      {/* Progress Indicators */}
      <div className="absolute bottom-8 left-10 lg:left-12 right-10 lg:right-12 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-4 relative z-20">
          <div className="text-ink/70 font-serif italic text-sm">
            {currentIndex + 1} <span className="opacity-50">/ {reviews.length}</span>
          </div>
          <div className="flex gap-2 items-center">
            <button 
              onClick={() => setIsPaused(!isPaused)}
              className={`w-8 h-8 rounded-full border flex items-center justify-center transition-colors cursor-pointer ${isPaused ? 'border-rule-3 text-rule-3 bg-rule-3/20' : 'border-ground-deep text-ink/70 hover:text-rule-3 hover:border-rule-3'}`}
              aria-label={isPaused ? "Play reviews" : "Pause reviews"}
            >
              {isPaused ? <Play className="w-3.5 h-3.5 ml-0.5" /> : <Pause className="w-3.5 h-3.5" />}
            </button>
            <button 
              onClick={() => {
                setCurrentIndex((prev) => (prev - 1 + reviews.length) % reviews.length);
                setIsPaused(true);
              }}
              className="w-8 h-8 rounded-full border border-ground-deep flex items-center justify-center text-ink/70 hover:text-rule-3 hover:border-rule-3 transition-colors cursor-pointer"
              aria-label="Previous review"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
            </button>
            <button 
              onClick={() => {
                setCurrentIndex((prev) => (prev + 1) % reviews.length);
                setIsPaused(true);
              }}
              className="w-8 h-8 rounded-full border border-ground-deep flex items-center justify-center text-ink/70 hover:text-rule-3 hover:border-rule-3 transition-colors cursor-pointer"
              aria-label="Next review"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
            </button>
          </div>
        </div>
        
        <a 
          href={`https://search.google.com/local/reviews?placeid=${PLACE_ID}`} 
          target="_blank" 
          rel="noopener noreferrer"
          className="text-sm font-semibold text-rule-3 hover:text-ink transition-colors flex items-center gap-1 group relative z-20"
        >
          Read all {reviews.length > 5 ? reviews.length : "40+"} reviews on Google
          <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
        </a>
      </div>
    </motion.div>
  );
}
